import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useSupabase } from '../../contexts/SupabaseContext';
import { Service } from '../../lib/services';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import type { Business } from '../../contexts/BusinessContext';
import { format } from 'date-fns';
import { createMobileBooking } from '../../lib/bookings';
import { findExistingCustomer, updateCustomerFromBooking } from '../../lib/customers';
import { triggerBookingAutomations } from '../../lib/automationsV2';

interface PlaceResult {
  formatted_address: string;
  address_components?: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
}

// Add a declaration to extend the Window interface
declare global {
  interface Window {
    google: any;
    initGoogleMapsCallback?: () => void;
  }
}

interface Category {
  id: string;
  category_title: string;
  sub_categories?: SubCategory[];
}

interface SubCategory {
  id: string;
  title: string;
  services: Array<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  }>;
}

interface ResourceAvailability {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  profile_photo?: string;
  resource_id?: string;
  postal_codes: {
    [key: string]: string[];
  };
  working_hours: {
    [key: string]: {
      areas: string[];
      radius: number;
      shifts: Array<{ start: string; end: string }>;
      enabled: boolean;
    };
  };
}

interface TimeSlot {
  time: Date;
  availableResources: Array<{
    id: string;
    resource_id?: string;
    name?: string;
    first_name: string;
    last_name: string;
    profile_photo?: string;
  }>;
}

type Step = 'location' | 'category' | 'service' | 'datetime' | 'customer';

// Add this interface for existing bookings
interface ExistingBooking {
  id: string;
  resource_id: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
}

// Add this interface for address components
interface AddressComponents {
  street_number: string;
  route: string;
  locality: string;
  administrative_area_level_1: string;
  postal_code: string;
  formatted_address: string;
}

// Add new interface for blocked time slots
interface BlockedTimeSlot {
  date: string;
  blockedSlots: string[];
}

interface BlockedTimeSlots {
  [resourceId: string]: BlockedTimeSlot[];
}

export default function BookOnline() {
  const [address, setAddress] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [error, setError] = useState('');
  const [isServiceable, setIsServiceable] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>('location');
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [expandedSubCategory, setExpandedSubCategory] = useState<string | null>(null);
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(null);
  const [availableResources, setAvailableResources] = useState<ResourceAvailability[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [selectedTimeForModal, setSelectedTimeForModal] = useState<TimeSlot | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const navigate = useNavigate();
  
  // Add state for blocked time slots
  const [blockedTimeSlots, setBlockedTimeSlots] = useState<BlockedTimeSlots>({});
  const [blockedSlotsLoading, setBlockedSlotsLoading] = useState(false);

  // Add state for existing bookings
  const [existingBookings, setExistingBookings] = useState<ExistingBooking[]>([]);
  
  // Add state for resource details
  const [resourceDetails, setResourceDetails] = useState<{[key: string]: {first_name: string; last_name: string; name?: string; avatar_url?: string}}>({});

  // Add buffer time state
  const [bufferTime, setBufferTime] = useState<number>(0);

  // Add session tracking state
  const [sessionId] = useState<string>(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Add these state variables to the component
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    email: '',
    phone: ''
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  useEffect(() => {
    async function loadBusiness() {
      if (!businessSlug) {
        navigate('/', { replace: true });
        return;
      }

      if (!supabase) {
        console.error('Supabase client is not initialized');
        setError('Unable to connect to the database');
        setIsLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('businesses')
          .select('*')
          .eq('slug', businessSlug)
          .single();

        if (fetchError || !data) {
          console.error('Error fetching business:', fetchError);
          setError('Business not found');
          setIsLoading(false);
          return;
        }

        console.log('Business data loaded:', {
          id: data.id,
          name: data.name,
          booking_settings: data.booking_settings,
          has_logo: !!data.booking_settings?.logo_url
        });

        setBusiness(data);
        
        // Fetch buffer time from online_booking_settings
        fetchBufferTime(data.id);
        
        // Track the visit to the online booking form
        trackBookingVisit(data.id);
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading business:', err);
        setError('Failed to load business information');
        setIsLoading(false);
      }
    }

    loadBusiness();
  }, [businessSlug, navigate]);

  // Add function to fetch buffer time
  const fetchBufferTime = async (businessId: string) => {
    if (!supabase) return;
    
    try {
      console.log('=== DIAGNOSTIC: Fetching buffer time for business:', businessId);
      
      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('buffer_time')
        .eq('business_id', businessId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found, use default of 0
          console.log('=== DIAGNOSTIC: No buffer time setting found, using default (0)');
          setBufferTime(0);
        } else {
          console.error('=== DIAGNOSTIC: Error fetching buffer time:', error);
          setBufferTime(0);
        }
        return;
      }
      
      if (data?.buffer_time !== undefined && data?.buffer_time !== null) {
        console.log(`=== DIAGNOSTIC: Setting buffer time to ${data.buffer_time} minutes from database`);
        setBufferTime(data.buffer_time);
      } else {
        console.log('=== DIAGNOSTIC: Buffer time value in database is undefined or null, using default (0)');
        setBufferTime(0);
      }
    } catch (err) {
      console.error('=== DIAGNOSTIC: Error fetching buffer time:', err);
      setBufferTime(0);
    }
  };

  // Function to track visits to the online booking form
  const trackBookingVisit = async (businessId: string) => {
    if (!supabase) return;
    
    try {
      // Gather visit information
      const visitData = {
        business_id: businessId,
        session_id: sessionId,
        step: 'location', // Initial step
        ip_address: null, // We can't get this from the frontend for privacy reasons
        user_agent: navigator.userAgent,
        referrer: document.referrer || null
      };

      const { error } = await supabase
        .from('online_booking_visits')
        .insert(visitData);

      if (error) {
        console.error('Error tracking booking visit:', error);
      } else {
        console.log('Online booking visit tracked successfully');
      }
    } catch (err) {
      console.error('Error tracking booking visit:', err);
    }
  };

  // Function to track step progression in the booking funnel
  const trackStepProgression = async (businessId: string, step: string) => {
    if (!supabase) return;
    
    try {
      const stepData = {
        business_id: businessId,
        session_id: sessionId,
        step: step,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null
      };

      const { error } = await supabase
        .from('booking_step_progression')
        .insert(stepData);

      if (error) {
        console.error('Error tracking step progression:', error);
      } else {
        console.log(`Step progression tracked: ${step}`);
      }
    } catch (err) {
      console.error('Error tracking step progression:', err);
    }
  };

  useEffect(() => {
    // Check if Google Maps API key is available
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.error('Google Maps API key is not set');
      setError('Google Maps API key is missing');
      return;
    }

    // Clean up any existing Google Maps script first to prevent conflicts
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.remove();
      console.log('Removed existing Google Maps script');
    }

    // Create a global callback function that will be called when the API is loaded
    window.initGoogleMapsCallback = () => {
      console.log('Google Maps API loaded successfully via callback');
      
      // Small delay to ensure the API is fully initialized
      setTimeout(() => {
        initAutocomplete();
        console.log('Autocomplete initialization triggered from callback');
      }, 100);
    };

    // Load the script with a callback
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMapsCallback`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';
    
    script.onerror = () => {
      console.error('Failed to load Google Maps API');
      setError('Unable to load address autocomplete');
    };
    
    document.head.appendChild(script);
    console.log('Added Google Maps script to head with callback');

    // Set up a retry mechanism in case the callback doesn't fire
    const retryTimeout = setTimeout(() => {
      if (window.google?.maps?.places) {
        console.log('Google Maps already loaded but callback not fired, initializing manually');
        initAutocomplete();
      } else {
        console.log('Google Maps failed to load within timeout, retrying...');
        // Remove script and try again
        document.querySelector('#google-maps-script')?.remove();
        document.head.appendChild(script);
      }
    }, 3000);

    return () => {
      // Clean up global callback
      if (window.initGoogleMapsCallback) {
        delete window.initGoogleMapsCallback;
      }
      
      // Clear retry timeout
      clearTimeout(retryTimeout);
      
      // Clean up existing autocomplete instance
      if (autoCompleteRef.current) {
        google.maps.event.clearInstanceListeners(autoCompleteRef.current);
        autoCompleteRef.current = null;
      }
    };
  }, []);

  // Add a new effect to reinitialize autocomplete when returning to the location step
  useEffect(() => {
    // When returning to the location step, reinitialize autocomplete if Google Maps is loaded
    if (currentStep === 'location' && window.google?.maps?.places && inputRef.current) {
      console.log('Returning to location step, reinitializing autocomplete');
      initAutocomplete();
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'category' && business?.id) {
      fetchCategories();
    }
  }, [currentStep, business?.id]);

  // Track step progression whenever currentStep changes
  useEffect(() => {
    if (business?.id && currentStep) {
      trackStepProgression(business.id, currentStep);
    }
  }, [currentStep, business?.id]);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }
      
      if (!business?.id) {
        throw new Error('Business not loaded');
      }
      
      const { data, error } = await supabase
        .from('online_booking_flow')
        .select('id, category_title, sub_categories')
        .eq('business_id', business.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Unable to load service categories');
    } finally {
      setIsLoading(false);
    }
  };

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places) {
      console.error('Input ref or Google Places not ready');
      return;
    }

    try {
      console.log('Initializing Google Maps Autocomplete...');
      
      // Clear any existing listeners to prevent duplicates
      if (autoCompleteRef.current) {
        console.log('Cleaning up existing autocomplete instance');
        google.maps.event.clearInstanceListeners(autoCompleteRef.current);
        autoCompleteRef.current = null;
      }
      
      // Create the autocomplete instance with specific fields
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'ca' },
        fields: [
          'address_components',
          'formatted_address',
          'geometry',
          'place_id'
        ],
        types: ['address']
      });

      // Add the place_changed listener
      if (autoCompleteRef.current) {
        autoCompleteRef.current.addListener('place_changed', () => {
          const place = autoCompleteRef.current?.getPlace();
          console.log('Selected place:', place);

          if (!place?.address_components) {
            setError('Please select a valid address from the dropdown');
            setIsServiceable(null);
            return;
          }

          // Initialize address components
          const addressComponents: AddressComponents = {
            street_number: '',
            route: '',
            locality: '',
            administrative_area_level_1: '',
            postal_code: '',
            formatted_address: place.formatted_address || ''
          };

          // Extract each component - improved extraction logic
          place.address_components.forEach(component => {
            // Check all types this component might match
            component.types.forEach(type => {
              // Handle postal_code specifically
              if (type === 'postal_code') {
                addressComponents.postal_code = component.long_name;
              } 
              // Handle other known types
              else if (type === 'street_number') {
                addressComponents.street_number = component.long_name;
              }
              else if (type === 'route') {
                addressComponents.route = component.long_name;
              }
              else if (type === 'locality') {
                addressComponents.locality = component.long_name;
              }
              else if (type === 'administrative_area_level_1') {
                addressComponents.administrative_area_level_1 = component.short_name;
              }
            });
          });

          console.log('Extracted address components:', addressComponents);

          // If we don't have a postal code, attempt to fetch it with a Places Details request
          if (!addressComponents.postal_code && place.place_id) {
            console.log('Postal code missing, attempting to fetch with Places Details');
            const placesService = new google.maps.places.PlacesService(document.createElement('div'));
            
            placesService.getDetails(
              { 
                placeId: place.place_id,
                fields: ['address_components']
              },
              (detailedPlace, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && detailedPlace?.address_components) {
                  console.log('Got detailed place info:', detailedPlace);
                  
                  // Extract postal code from detailed place
                  let postalCode = '';
                  detailedPlace.address_components.forEach(component => {
                    if (component.types.includes('postal_code')) {
                      postalCode = component.long_name;
                    }
                  });
                  
                  if (postalCode) {
                    console.log('Found postal code in detailed request:', postalCode);
                    addressComponents.postal_code = postalCode;
                    processAddress(addressComponents);
                  } else {
                    setError('Unable to find postal code for this address. Please enter a complete address.');
                    setIsServiceable(null);
                  }
                } else {
                  console.error('Failed to get detailed place info:', status);
                  setError('Unable to verify the complete address. Please try again or enter a different address.');
                  setIsServiceable(null);
                }
              }
            );
          } else {
            // We have all components, process the address
            processAddress(addressComponents);
          }
        });
      }

      console.log('Autocomplete initialization complete');
    } catch (err) {
      console.error('Error initializing Google Places Autocomplete:', err);
      setError('Unable to initialize address autocomplete');
    }
  };

  // Separate function to process address after components are extracted
  const processAddress = (addressComponents: AddressComponents) => {
    try {
      console.log('Processing address with components:', addressComponents);
      
      // Ensure postal code is properly formatted if present
      if (addressComponents.postal_code) {
        // Remove spaces and convert to uppercase for Canadian postal codes
        addressComponents.postal_code = addressComponents.postal_code
          .replace(/\s+/g, '')
          .toUpperCase()
          .match(/^[A-Z]\d[A-Z]\d[A-Z]\d$/) 
            // Add a space in the middle if it's a valid Canadian postal code
            ? `${addressComponents.postal_code.slice(0, 3)} ${addressComponents.postal_code.slice(3)}`
            : addressComponents.postal_code;
      }

      // Validate required components
      if (!addressComponents.street_number || !addressComponents.route || 
          !addressComponents.locality || !addressComponents.administrative_area_level_1 || 
          !addressComponents.postal_code) {
        setError('Please select a complete address with all components including postal code');
        setIsServiceable(null);
        return;
      }

      // Format the address consistently
      const formattedAddress = `${addressComponents.street_number} ${addressComponents.route}, ${addressComponents.locality}, ${addressComponents.administrative_area_level_1} ${addressComponents.postal_code}`;

      // Update state with the complete address
      setError('');
      setAddress(formattedAddress);
      // Keep postal code in raw format (no spaces) for lookup
      const rawPostalCode = addressComponents.postal_code.replace(/\s+/g, '');
      setPostalCode(rawPostalCode);

      // If there's a unit number, prepend it
      if (unitNumber) {
        setAddress(`Unit ${unitNumber}, ${formattedAddress}`);
      }

      // Check if the postal code is serviceable
      checkPostalCodeAvailability(addressComponents.postal_code);

      console.log('Final address set:', {
        fullAddress: formattedAddress,
        postalCode: addressComponents.postal_code,
        rawPostalCode,
        unitNumber: unitNumber || 'N/A'
      });
    } catch (err) {
      console.error('Error processing address:', err);
      setError('Error processing the selected address. Please try again.');
      setIsServiceable(null);
    }
  };

  // Add this function to handle unit number changes
  const handleUnitNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUnitNumber(value);
    
    // If we already have an address, update it with the new unit number
    if (address) {
      const baseAddress = address.replace(/^Unit \d+,\s*/, ''); // Remove existing unit number if any
      setAddress(value ? `Unit ${value}, ${baseAddress}` : baseAddress);
    }
  };

  const checkPostalCodeAvailability = async (postalCode: string) => {
    if (!supabase) return false;
    
    try {
      setIsLoading(true);
      // Get first 3 characters of postal code
      const postalPrefix = postalCode.substring(0, 3).toUpperCase();
      
      // Get current day of week in lowercase
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const currentDay = days[new Date().getDay()];

      // Query mobile_availability table
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('postal_codes')
        .eq('mobile_available', true);

      if (error) throw error;

      // Check if postal code exists in any resource's postal codes for any day
      const isAvailable = data?.some(record => {
        const postalCodes = record.postal_codes as Record<string, string[]>;
        return Object.values(postalCodes).some(codes => 
          codes.some(code => code.toUpperCase() === postalPrefix)
        );
      });

      setIsServiceable(!!isAvailable);
      return !!isAvailable;
    } catch (err) {
      console.error('Error checking postal code availability:', err);
      setError('Unable to check service availability');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    switch (currentStep) {
      case 'location':
        if (!isServiceable) {
          setError('Please enter a valid serviceable address');
          return;
        }
        setCurrentStep('category');
        break;
      case 'category':
        if (!selectedCategory) {
          setError('Please select a category');
          return;
        }
        setCurrentStep('service');
        break;
      case 'service':
        if (!selectedService) {
          setError('Please select a service');
          return;
        }
        setCurrentStep('datetime');
        break;
      case 'datetime':
        if (!selectedDate || !selectedTime) {
          setError('Please select both date and time');
          return;
        }
        setCurrentStep('customer');
        break;
      case 'customer':
        // Handle customer info submission
        break;
    }
    setError('');
  };

  const handleStepClick = (clickedStep: Step) => {
    const currentStepIndex = steps.findIndex(s => s.step === currentStep);
    const clickedStepIndex = steps.findIndex(s => s.step === clickedStep);

    // Only allow clicking on previous steps
    if (clickedStepIndex >= currentStepIndex) return;

    // Reset states based on which step we're moving back to
    if (clickedStepIndex < steps.findIndex(s => s.step === 'datetime')) {
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedResourceId(null);
      setAvailableTimeSlots([]);
    }
    
    if (clickedStepIndex < steps.findIndex(s => s.step === 'service')) {
      setSelectedService(null);
      setExpandedServiceId(null);
      setExpandedSubCategory(null);
    }
    
    if (clickedStepIndex < steps.findIndex(s => s.step === 'category')) {
      setSelectedCategory(null);
    }
    
    if (clickedStepIndex < steps.findIndex(s => s.step === 'location')) {
      setAddress('');
      setUnitNumber('');
      setPostalCode('');
      setIsServiceable(null);
    }

    setCurrentStep(clickedStep);
    setError('');
  };

  const steps = [
    { step: 'location' as const, label: 'Location' },
    { step: 'category' as const, label: 'Category' },
    { step: 'service' as const, label: 'Service' },
    { step: 'datetime' as const, label: 'Date & Time' },
    { step: 'customer' as const, label: 'Customer Info' }
  ];

  const renderLocationStep = () => {
    return (
      <div className="space-y-4">
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            Service Address
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                ref={inputRef}
                type="text"
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Start typing your address and select from dropdown"
                className="w-full h-10 px-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
            </div>
            <div className="w-32">
              <input
                type="text"
                id="unit"
                value={unitNumber}
                onChange={handleUnitNumberChange}
                placeholder="Unit #"
                className="w-full h-10 px-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="text-red-600 text-sm">{error}</div>
        )}

        {isServiceable !== null && (
          <div className={`text-sm ${isServiceable ? 'text-green-600' : 'text-red-600'}`}>
            {isServiceable 
              ? '✓ This address is within our service area'
              : '✗ Sorry, we currently don\'t service this area'}
          </div>
        )}
      </div>
    );
  };

  const renderCategoryStep = () => {
    return (
      <div className="max-w-xl mx-auto px-2">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        {isLoading ? (
            <div className="text-center col-span-full">Loading categories...</div>
        ) : error ? (
            <div className="text-center col-span-full text-red-600">{error}</div>
        ) : categories.length === 0 ? (
            <div className="text-center col-span-full text-gray-600">No service categories available</div>
        ) : (
            categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-3 sm:p-4 rounded-lg border-2 transition-all text-sm sm:text-base ${
                  selectedCategory === category.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                }`}
              >
                <h3 className="font-medium">{category.category_title}</h3>
              </button>
            ))
        )}
        </div>
        {error && <p className="mt-4 text-center text-red-600">{error}</p>}
      </div>
    );
  };

  const renderServiceStep = () => {
    const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
    return (
      <div className="max-w-xl mx-auto">
        {isLoading ? (
          <div className="text-center">Loading services...</div>
        ) : error ? (
          <div className="text-center text-red-600">{error}</div>
        ) : !selectedCategoryData?.sub_categories?.length ? (
          <div className="text-center text-gray-600">No services available in this category</div>
        ) : (
          <div className="space-y-6">
            {/* Sub-category buttons */}
            <div className="flex flex-wrap justify-center gap-2">
              {selectedCategoryData.sub_categories.map((subCategory) => (
                <button
                  key={subCategory.id}
                  onClick={() => setExpandedSubCategory(
                    expandedSubCategory === subCategory.id ? null : subCategory.id
                  )}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    expandedSubCategory === subCategory.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {subCategory.title}
                </button>
              ))}
            </div>

            {/* Services for selected sub-category */}
            {expandedSubCategory && (
              <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-4">
                {selectedCategoryData.sub_categories
                  .find(sub => sub.id === expandedSubCategory)
                  ?.services.map((service) => (
                    <div 
                      key={`${service.service.id}-${service.priceOption.id}`} 
                      className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-3"
                    >
                      <h4 className="text-lg sm:text-xl font-medium text-gray-900 italic">{service.service.name}</h4>
                      
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {service.priceOption.duration_minutes} MINS
                      </span>
                      
                        <p className="text-gray-900 text-sm sm:text-base">Price ${service.priceOption.price}</p>
                      </div>
                      
                      <div>
                        {expandedServiceId === service.service.id && (
                          <p className="mt-2 text-sm text-gray-600">{service.service.description || 'No description available.'}</p>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedServiceId(
                              expandedServiceId === service.service.id ? null : service.service.id
                            );
                          }}
                          className="w-full text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                        >
                          {expandedServiceId === service.service.id ? 'Hide Description' : 'Show Description'}
                        </button>
                        
                        <button
                          onClick={() => {
                            setSelectedService(service);
                            setCurrentStep('datetime');
                          }}
                          className={`w-full py-2 text-center font-medium rounded-md transition-colors text-sm ${
                            selectedService?.service.id === service.service.id &&
                            selectedService?.priceOption.id === service.priceOption.id
                              ? 'bg-blue-500 text-white'
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                          }`}
                        >
                          SELECT
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
        {error && <p className="mt-4 text-center text-red-600">{error}</p>}
      </div>
    );
  };

  // Add function to fetch resource details
  const fetchResourceDetails = async (resourceIds: string[]) => {
    if (!supabase || resourceIds.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('id, first_name, last_name, avatar_url')
        .in('id', resourceIds);
        
      if (error) {
        console.error('Error fetching resource details:', error);
        return;
      }
      
      const detailsMap: {[key: string]: {first_name: string; last_name: string; name?: string; avatar_url?: string}} = {};
      
      if (data && data.length > 0) {
        data.forEach(resource => {
          detailsMap[resource.id] = {
            first_name: resource.first_name,
            last_name: resource.last_name,
            name: `${resource.first_name} ${resource.last_name}`.trim(),
            avatar_url: resource.avatar_url || undefined
          };
        });
      }
      
      setResourceDetails(detailsMap);
      console.log('=== DIAGNOSTIC: Fetched resource details:', detailsMap);
    } catch (err) {
      console.error('Error in fetchResourceDetails:', err);
    }
  };

  const fetchAvailableResources = async () => {
    if (!supabase || !postalCode || !business?.id) return;

    try {
      console.log('=== DIAGNOSTIC: Fetching available resources for postal code:', postalCode, 'and business:', business.id);
      
      // First get mobile availability records
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('id, first_name, last_name, postal_codes, working_hours, mobile_available, resource_id')
        .eq('business_id', business.id)
        .eq('mobile_available', true);

      if (error) {
        console.error('Error fetching available resources:', error);
        throw error;
      }
      
      console.log('=== DIAGNOSTIC: Raw resource data from database:', data);
      console.log('=== DIAGNOSTIC: Number of resources found for this business:', data ? data.length : 0);
      
      if (!data || data.length === 0) {
        console.log('=== DIAGNOSTIC: No resources found for this business');
        setAvailableResources([]);
        return;
      }

      // Collect resource_ids to fetch detailed information
      const resourceIds = data
        .map(resource => resource.resource_id)
        .filter(id => id) as string[];
      
      // Fetch resource details if we have any valid resource_ids
      if (resourceIds.length > 0) {
        await fetchResourceDetails(resourceIds);
      }

      // Map the data to include all necessary fields
      const resourcesWithNames = data.map(resource => {
        console.log('=== DIAGNOSTIC: Processing resource for business', business.id, ':', {
          id: resource.id,
          first_name: resource.first_name,
          last_name: resource.last_name,
          mobile_available: resource.mobile_available,
          resource_id: resource.resource_id || 'NOT_FOUND'
        });
        
        // Get resource details from our fetched data if available
        const details = resource.resource_id ? resourceDetails[resource.resource_id] : null;
        
        return {
          id: resource.id, // This is the mobile_availability.id
          resource_id: resource.resource_id, // This is the id used in bookings table
          // Use resource table data if available, otherwise fall back to mobile_availability data
          first_name: details?.first_name || resource.first_name,
          last_name: details?.last_name || resource.last_name,
          name: details?.name || `${resource.first_name} ${resource.last_name}`.trim(),
          profile_photo: details?.avatar_url, // Only use avatar_url from resources
          postal_codes: resource.postal_codes,
          working_hours: resource.working_hours
        };
      });
      
      console.log('=== DIAGNOSTIC: Processed resources for business', business.id, ':', resourcesWithNames.length);
      setAvailableResources(resourcesWithNames);
    } catch (err) {
      console.error('Error fetching available resources:', err);
      setError('Unable to fetch available resources');
    }
  };

  useEffect(() => {
    if (currentStep === 'datetime' && postalCode) {
      fetchAvailableResources();
    }
  }, [currentStep, postalCode]);

  const getDayName = (date: Date): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  const isPostalCodeAvailableForDay = (
    resource: ResourceAvailability,
    postalPrefix: string,
    dayName: string
  ): boolean => {
    const postalCodes = resource.postal_codes[dayName] || [];
    return postalCodes.some(code => postalPrefix.startsWith(code));
  };

  // Add this function to fetch all future bookings for the business
  const fetchExistingBookings = async () => {
    if (!supabase || !business?.id) return;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      console.log('=== DIAGNOSTIC: Fetching bookings for business ID:', business.id, 'from date:', today.toISOString());
      
      const { data, error } = await supabase
        .from('mobile_bookings')
        .select('id, resource_id, start_date, start_time, end_date, end_time')
        .eq('business_id', business.id)
        .gte('start_date', today.toISOString().split('T')[0])
        .order('start_date', { ascending: true });

      if (error) throw error;

      console.log('=== DIAGNOSTIC: Fetched existing bookings:', data);
      console.log('=== DIAGNOSTIC: Number of bookings fetched:', data ? data.length : 0);
      
      // Process the bookings into blocked time slots
      const blocked: BlockedTimeSlots = {};

      if (data && data.length > 0) {
        data.forEach(booking => {
          const { resource_id, start_date, start_time, end_time } = booking;
          
          // Skip if resource_id is not available
          if (!resource_id) return;

          // Initialize array for this resource if it doesn't exist
          if (!blocked[resource_id]) {
            blocked[resource_id] = [];
          }

          // Find or create the entry for this date
          let dateEntry = blocked[resource_id].find((entry: BlockedTimeSlot) => entry.date === start_date);
          if (!dateEntry) {
            dateEntry = { date: start_date, blockedSlots: [] };
            blocked[resource_id].push(dateEntry);
          }

          // Calculate all 30-minute slots between start_time and end_time
          const [startHour, startMinute] = start_time.split(':').map(Number);
          const [endHour, endMinute] = end_time.split(':').map(Number);
          const startMinutes = startHour * 60 + startMinute;
          const endMinutes = endHour * 60 + endMinute;

          // Generate all 30-minute slots that are blocked
          for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
            const hour = Math.floor(minutes / 60);
            const minute = minutes % 60;
            const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            if (!dateEntry.blockedSlots.includes(timeSlot)) {
              dateEntry.blockedSlots.push(timeSlot);
            }
          }
        });
      }

      console.log('=== DIAGNOSTIC: Blocked time slots:', blocked);
      setBlockedTimeSlots(blocked);
      setExistingBookings(data || []);
    } catch (err) {
      console.error('Error fetching existing bookings:', err);
      toast.error('Failed to load existing bookings');
    }
  };

  // Add effect to fetch bookings when component mounts
  useEffect(() => {
    if (business?.id) {
      console.log('=== DIAGNOSTIC: Business ID available, fetching bookings');
      fetchExistingBookings();
    }
  }, [business?.id]);

  // Update isTimeSlotOverlapping function using the improved time comparison logic
  const isTimeSlotOverlapping = (
    mobileAvailabilityId: string,
    proposedStart: Date,
    proposedEnd: Date
  ): boolean => {
    console.log('=== DIAGNOSTIC: Checking overlap for mobile_availability ID:', {
      mobileAvailabilityId,
      proposedStart: proposedStart.toISOString(),
      proposedEnd: proposedEnd.toISOString(),
      proposedDate: proposedStart.toDateString()
    });

    // Find the resource in our available resources list to get the mapped resource_id
    const resource = availableResources.find(r => r.id === mobileAvailabilityId);
    if (!resource) {
      console.log('=== DIAGNOSTIC: Resource not found in available resources!');
      return false;
    }
    
    // Get the actual resource_id used in the bookings table
    const bookingResourceId = resource.resource_id;
    
    if (!bookingResourceId) {
      console.log('=== DIAGNOSTIC: No resource_id mapping found for this resource, cannot check for overlaps');
      return false;
    }
    
    // Check using the blocked time slots approach
    const localDate = new Date(proposedStart);
    const dateStr = format(localDate, 'yyyy-MM-dd');
    
    console.log('=== DIAGNOSTIC: Using resource_id for booking lookup:', bookingResourceId, 'date:', dateStr);

    // If we don't have blocked slots for this resource_id, it's available
    if (!blockedTimeSlots[bookingResourceId]) {
      console.log('=== DIAGNOSTIC: No blocked slots for this resource, slot is available');
      return false;
    }

    // Look for blocked slots for this date
    const dateEntry = blockedTimeSlots[bookingResourceId].find((entry: BlockedTimeSlot) => entry.date === dateStr);
    if (!dateEntry) {
      console.log('=== DIAGNOSTIC: No blocked slots for this date, slot is available');
      return false;
    }

    // Calculate total duration for the proposed booking
    const serviceDuration = selectedService?.priceOption.duration_minutes || 0;

    // Convert booking start time to minutes since midnight
    const bookingStartMinutes = proposedStart.getHours() * 60 + proposedStart.getMinutes();
    const bookingEndMinutes = bookingStartMinutes + serviceDuration;

    // Convert blocked slots to minute ranges
    const blockedRanges = dateEntry.blockedSlots.map((slot: string) => {
      const [hour, minute] = slot.split(':').map(Number);
      const slotMinutes = hour * 60 + minute;
      return {
        start: slotMinutes,
        end: slotMinutes + 30 // Each slot is 30 minutes
      };
    });

    // Check if booking overlaps with any blocked range
    const hasOverlap = blockedRanges.some((range: { start: number; end: number }) => {
      // Booking starts before blocked slot ends AND booking ends after blocked slot starts
      const overlaps = (bookingStartMinutes < range.end && bookingEndMinutes > range.start);
      if (overlaps) {
        console.log('=== DIAGNOSTIC: Found overlap with blocked slot:', range);
      }
      return overlaps;
    });

    if (hasOverlap) {
      console.log('=== DIAGNOSTIC: Slot overlaps with existing booking, not available');
    } else {
      console.log('=== DIAGNOSTIC: No overlap found, slot is available');
    }

    return hasOverlap;
  };

  // Modify the getAvailableTimeSlots function to use more precise logging
  const getAvailableTimeSlots = async (date: Date) => {
    if (!postalCode || !selectedService || !supabase) return [];

    console.log('=== DIAGNOSTIC: Generating time slots for date:', date.toLocaleDateString(), date.toISOString());
    console.log('=== DIAGNOSTIC: Existing bookings count:', existingBookings.length);
    console.log('=== DIAGNOSTIC: Buffer time:', bufferTime, 'minutes');

    const dayName = getDayName(date);
    const postalPrefix = postalCode.substring(0, 3).toUpperCase();
    const slotMap = new Map<string, TimeSlot>();
    const serviceDuration = selectedService.priceOption.duration_minutes;
    // Calculate total duration including buffer time
    const totalDuration = serviceDuration + bufferTime;
    console.log('=== DIAGNOSTIC: Total duration (service + buffer):', totalDuration, 'minutes');
    const now = new Date();

    try {
      // Find resources that serve this postal code on this day
      const availableResourcesForDay = availableResources.filter(resource => {
        const daySchedule = resource.working_hours[dayName];
        return (
          isPostalCodeAvailableForDay(resource, postalPrefix, dayName) &&
          daySchedule?.enabled &&
          daySchedule?.shifts?.length > 0
        );
      });

      console.log('=== DIAGNOSTIC: Available resources for day:', availableResourcesForDay.map(r => ({
        id: r.id, // mobile_availability.id
        resource_id: r.resource_id, // This is the critical ID used in bookings
        name: r.name,
        shifts: r.working_hours[dayName]?.shifts
      })));

      if (availableResourcesForDay.length === 0) return [];

      // Format date for blocked slot lookup
      const dateStr = format(date, 'yyyy-MM-dd');

      // Generate time slots for each resource
      availableResourcesForDay.forEach(resource => {
        // Skip resources with no resource_id mapping
        if (!resource.resource_id) {
          console.log(`=== DIAGNOSTIC: Skipping resource ${resource.id} (${resource.name}) with no resource_id mapping`);
          return;
        }
        
        const daySchedule = resource.working_hours[dayName];

        daySchedule.shifts.forEach(shift => {
          const [startHour, startMinute] = shift.start.split(':').map(Number);
          const [endHour, endMinute] = shift.end.split(':').map(Number);

          const shiftStart = new Date(date);
          shiftStart.setHours(startHour, startMinute, 0, 0);

          const shiftEnd = new Date(date);
          shiftEnd.setHours(endHour, endMinute, 0, 0);

          console.log('=== DIAGNOSTIC: Processing shift for resource:', {
            id: resource.id, // mobile_availability.id (for reference only)
            resource_id: resource.resource_id, // The ID used for booking lookups
            resourceName: resource.name,
            shiftStart: shiftStart.toISOString(),
            shiftEnd: shiftEnd.toISOString(),
            serviceDuration,
            totalDuration
          });

          // Get blocked slots for this resource
          const resourceBlockedSlots = blockedTimeSlots[resource.resource_id || ''];
          const dateBlockedSlots = resourceBlockedSlots
            ? resourceBlockedSlots.find((entry: BlockedTimeSlot) => entry.date === dateStr)?.blockedSlots || []
            : [];
            
          console.log('=== DIAGNOSTIC: Blocked slots for this resource/date:', dateBlockedSlots);

          // Convert blocked slots to minute ranges for easier comparison
          const blockedRanges = dateBlockedSlots.map((slot: string) => {
            const [hour, minute] = slot.split(':').map(Number);
            const slotMinutes = hour * 60 + minute;
            return {
              start: slotMinutes,
              end: slotMinutes + 30 // Each slot is 30 minutes
            };
          });

          // Generate 30-minute slots
          const current = new Date(shiftStart);
          while (current <= shiftEnd) {
            // Skip slots in the past
            if (current <= now) {
              current.setMinutes(current.getMinutes() + 30);
              continue;
            }

            // Calculate service end time (including buffer)
            const serviceEnd = new Date(current);
            // Use total duration (service + buffer) for end time calculation
            serviceEnd.setMinutes(serviceEnd.getMinutes() + totalDuration);

            // Skip if service would end after shift end
            if (serviceEnd > shiftEnd) {
              break;
            }

            // Convert current time to minutes since midnight
            const slotStartMinutes = current.getHours() * 60 + current.getMinutes();
            // Use total duration (service + buffer) for end time calculation
            const slotEndMinutes = slotStartMinutes + totalDuration;

            console.log('=== DIAGNOSTIC: Checking availability for potential slot:', {
              start: current.toISOString(),
              end: serviceEnd.toISOString(),
              resourceId: resource.id,
              resource_id: resource.resource_id,
              slotStartMinutes,
              slotEndMinutes,
              includesBuffer: bufferTime > 0
            });

            // Check if this slot overlaps with any blocked range
            const hasOverlap = blockedRanges.some((range: { start: number; end: number }) => {
              // Slot starts before blocked range ends AND slot ends after blocked range starts
              return (slotStartMinutes < range.end && slotEndMinutes > range.start);
            });

            const isAvailable = !hasOverlap;
            console.log('=== DIAGNOSTIC: Slot available?', isAvailable);

            if (isAvailable) {
              const timeKey = current.toISOString();
              const existingSlot = slotMap.get(timeKey) || {
                time: new Date(current),
                availableResources: []
              };

              existingSlot.availableResources.push({
                id: resource.id,
                resource_id: resource.resource_id, // Include this for later use
                first_name: resource.first_name,
                last_name: resource.last_name,
                name: resource.name
              });

              slotMap.set(timeKey, existingSlot);
              console.log('=== DIAGNOSTIC: Added available slot:', {
                time: current.toISOString(),
                resource: resource.name,
                resource_id: resource.resource_id
              });
            }

            current.setMinutes(current.getMinutes() + 30);
          }
        });
      });

      const timeSlots = Array.from(slotMap.values())
        .sort((a, b) => a.time.getTime() - b.time.getTime());

      console.log('=== DIAGNOSTIC: Generated available time slots:', timeSlots.length);
      console.log('=== DIAGNOSTIC: First few time slots:', timeSlots.slice(0, 3).map(slot => ({
        time: slot.time.toISOString(),
        resourcesCount: slot.availableResources.length,
        resources: slot.availableResources.map(r => ({ 
          id: r.id, 
          resource_id: r.resource_id,
          name: r.name 
        }))
      })));
      
      return timeSlots;
    } catch (err) {
      console.error('=== DIAGNOSTIC: Error generating time slots:', err);
      return [];
    }
  };

  const handleDateSelect = async (date: Date | null) => {
    setSelectedDate(date);
    setSelectedTime(null);
    if (date) {
      console.log('=== DIAGNOSTIC: Date selected:', date.toISOString(), 'Fetching time slots...');
      const slots = await getAvailableTimeSlots(date);
      console.log('=== DIAGNOSTIC: Time slots fetched:', slots.length);
      setAvailableTimeSlots(slots);
    } else {
      setAvailableTimeSlots([]);
    }
  };

  const handleTimeSlotClick = (timeSlot: TimeSlot) => {
    console.log('=== DIAGNOSTIC: Time slot clicked:', {
      time: timeSlot.time,
      resources: timeSlot.availableResources.map(r => ({
        id: r.id,
        resource_id: r.resource_id, // Log resource_id for debugging
        first_name: r.first_name,
        last_name: r.last_name,
        name: r.name
      }))
    });

    if (timeSlot.availableResources.length === 1) {
      // Auto-select if only one resource is available
      setSelectedTime(timeSlot.time);
      // Use resource_id here if it exists, otherwise fall back to id
      setSelectedResourceId(timeSlot.availableResources[0].resource_id || timeSlot.availableResources[0].id);
      setSelectedTimeForModal(timeSlot);
      console.log('=== DIAGNOSTIC: Auto-selected resource:', {
        ...timeSlot.availableResources[0],
        using_id: timeSlot.availableResources[0].resource_id ? 'resource_id' : 'mobile_availability.id'
      });
    } else {
      // Show modal for resource selection
      setSelectedTimeForModal(timeSlot);
      setIsResourceModalOpen(true);
      console.log('=== DIAGNOSTIC: Opening modal with resources:', timeSlot.availableResources);
    }
  };

  const handleResourceSelect = (resourceId: string) => {
    if (selectedTimeForModal) {
      setSelectedTime(selectedTimeForModal.time);
      setSelectedResourceId(resourceId);
      setIsResourceModalOpen(false);
      setSelectedTimeForModal(null);
    }
  };

  const ResourceSelectionModal = () => {
    if (!selectedTimeForModal) return null;

    console.log('=== DIAGNOSTIC: Rendering modal with resources:', selectedTimeForModal.availableResources);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold mb-4">Select Available Resource</h3>
          <p className="text-sm text-gray-600 mb-4">
            {selectedTimeForModal.time.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </p>
          <div className="space-y-2">
            {selectedTimeForModal.availableResources.map(resource => {
              console.log('=== DIAGNOSTIC: Rendering resource in modal:', {
                id: resource.id,
                resource_id: resource.resource_id || 'NOT_FOUND',
                name: resource.name || `${resource.first_name || ''} ${resource.last_name || ''}`.trim()
              });
              
              // Get resource details from our fetched data if available
              const details = resource.resource_id ? resourceDetails[resource.resource_id] : null;
              const firstName = details?.first_name || resource.first_name;
              const lastName = details?.last_name || resource.last_name;
              const profilePhoto = details?.avatar_url || resource.profile_photo;
              
              const initials = getInitials(firstName, lastName);
              // Use resource_id if available (for booking), otherwise fall back to id
              const idToUse = resource.resource_id || resource.id;
              return (
                <button
                  key={resource.id}
                  onClick={() => handleResourceSelect(idToUse)}
                  className="w-full py-3 px-4 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center space-x-3"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium overflow-hidden">
                    {profilePhoto ? (
                      <img src={profilePhoto} alt={initials} className="h-full w-full object-cover" />
                    ) : (
                      initials
                    )}
                  </div>
                  <span>
                    {details?.name || resource.name || 
                     `${firstName || ''} ${lastName || ''}`.trim() || 
                     `Resource ${resource.id.slice(0, 4)}`}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setIsResourceModalOpen(false)}
            className="mt-4 w-full py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderDateTimeStep = () => {
    const today = new Date();

    const isDateAvailable = (date: Date): boolean => {
      if (!postalCode || !selectedService) return false;
      
      // Check if date is in the past (before today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      if (checkDate < today) return false;

      const dayName = getDayName(date);
      const postalPrefix = postalCode.substring(0, 3).toUpperCase();
      const serviceDuration = selectedService.priceOption.duration_minutes;
      const now = new Date();
      
      // Check if any resource is available for this day and postal code
      const availableResourcesForDay = availableResources.filter(resource => {
        const daySchedule = resource.working_hours[dayName];
        const isAvailable = isPostalCodeAvailableForDay(resource, postalPrefix, dayName) &&
          daySchedule?.enabled &&
          daySchedule?.shifts?.length > 0;

        if (!isAvailable) return false;

        // For each shift, check if there would be any valid time slots
        return daySchedule.shifts.some(shift => {
          const [startHour, startMinute] = shift.start.split(':').map(Number);
          const [endHour, endMinute] = shift.end.split(':').map(Number);

          const startTime = new Date(date);
          startTime.setHours(startHour, startMinute, 0);

          const endTime = new Date(date);
          endTime.setHours(endHour, endMinute, 0);

          // Adjust end time to account for service duration
          const lastPossibleStartTime = new Date(endTime);
          lastPossibleStartTime.setMinutes(lastPossibleStartTime.getMinutes() - serviceDuration);

          // For today, check if there are any future slots available
          if (date.toDateString() === today.toDateString()) {
            return lastPossibleStartTime > now && startTime < lastPossibleStartTime;
          }

          // For future dates, just check if there's enough time for at least one slot
          return startTime < lastPossibleStartTime;
        });
      });

      return availableResourcesForDay.length > 0;
    };

    const renderDayContents = (day: number, date: Date) => {
      const hasTimeSlots = isDateAvailable(date);
      
      return (
        <div className="relative flex flex-col items-center">
          <div>{day}</div>
          {hasTimeSlots && (
            <div className="absolute -bottom-2 w-1.5 h-1.5 rounded-full bg-blue-500"></div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4 sm:space-y-6 px-2">
        <style>
          {`
            .react-datepicker__navigation {
              background: none;
              line-height: 1.7rem;
              text-align: center;
              cursor: pointer;
              position: absolute;
              top: 2px;
              width: 0;
              padding: 0;
              border: 0.45rem solid transparent;
              z-index: 1;
              height: 0.45rem;
              width: 0.45rem;
              text-indent: -999em;
              overflow: hidden;
            }
            .react-datepicker__navigation--previous {
              left: 2px;
              border-right-color: #ccc;
            }
            .react-datepicker__navigation--next {
              right: 2px;
              border-left-color: #ccc;
            }
            .react-datepicker__navigation:hover *::before {
              border-color: #a6a6a6;
            }
            .react-datepicker__navigation-icon {
              position: relative;
              top: -1px;
              font-size: 20px;
              width: 0;
            }
            .react-datepicker__navigation-icon--previous {
              left: 2px;
            }
            .react-datepicker__navigation-icon--next {
              left: -2px;
            }
            .react-datepicker__navigation-icon--previous:before {
              content: "";
              border-color: #ccc;
              border-style: solid;
              border-width: 3px 3px 0 0;
              display: block;
              height: 9px;
              width: 9px;
              position: absolute;
              top: 6px;
              transform: rotate(225deg);
              right: 0px;
            }
            .react-datepicker__navigation-icon--next:before {
              content: "";
              border-color: #ccc;
              border-style: solid;
              border-width: 3px 3px 0 0;
              display: block;
              height: 9px;
              width: 9px;
              position: absolute;
              top: 6px;
              transform: rotate(45deg);
              left: 0px;
            }
          `}
        </style>
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Select Date
              </label>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateSelect}
                minDate={today}
                inline
                forceShowMonthNavigation
                showDisabledMonthNavigation
                calendarClassName="border border-gray-200 rounded-lg"
                filterDate={isDateAvailable}
                renderDayContents={renderDayContents}
                dayClassName={() => "relative py-1 sm:py-2"}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Available Time Slots
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 border border-gray-200 rounded-lg p-2 sm:p-4">
                {availableTimeSlots.length > 0 ? (
                  availableTimeSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeSlotClick(slot)}
                      className={`
                        relative py-1.5 sm:py-2 px-2 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors
                        ${slot.time === selectedTime
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-50 text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <div>
                        {slot.time.toLocaleTimeString('en-US', { 
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </div>
                      {slot.availableResources.length > 1 && (
                        <div className="text-xs mt-0.5">
                          {slot.availableResources.length} available
                        </div>
                      )}
                    </button>
                  ))
                ) : (
                  <p className="col-span-2 sm:col-span-3 text-center text-gray-500 text-xs sm:text-sm py-4">
                    {selectedDate ? 'No available time slots for this date' : 'Select a date to see available times'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Add service summary section */}
        {selectedService && currentStep !== 'customer' && (
          <div className="p-3 sm:p-4 bg-gray-50 rounded-lg text-sm">
            <h3 className="font-semibold mb-2">Service Summary</h3>
            <div className="space-y-1 sm:space-y-2">
              <div>
                <span className="font-medium">Service: </span>
                {selectedService.service.name}
              </div>
              <div>
                <span className="font-medium">Price: </span>
                ${selectedService.priceOption.price.toFixed(2)}
              </div>
              <div>
                <span className="font-medium">Duration: </span>
                {selectedService.priceOption.duration_minutes} minutes
              </div>
              
              {selectedTime && selectedResourceId ? (
                <>
                  <div className="pt-1 sm:pt-2 border-t mt-1 sm:mt-2">
                    <span className="font-medium">Date: </span>
                    {new Date(selectedTime).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long', 
                      day: 'numeric'
                    })}
                  </div>
                  
                  <div>
                    <span className="font-medium">Time: </span>
                    {(() => {
                      const startTime = new Date(selectedTime);
                      const endTime = new Date(startTime.getTime() + selectedService.priceOption.duration_minutes * 60000);
                      
                      return (
                        <>
                          {startTime.toLocaleTimeString('en-US', { 
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true 
                          })} - {endTime.toLocaleTimeString('en-US', { 
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </>
                      );
                    })()}
                  </div>
                  
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className="font-medium">Technician: </span>
                    {(() => {
                      const selectedResource = (() => {
                        // First try to find by resource_id (preferred)
                        const byResourceId = availableResources.find(r => r.resource_id === selectedResourceId);
                        if (byResourceId) return byResourceId;
                        
                        // If not found, try by mobile_availability.id
                        return availableResources.find(r => r.id === selectedResourceId);
                      })();
                      
                      // Get resource details from our fetched data
                      const details = selectedResource?.resource_id 
                        ? resourceDetails[selectedResource.resource_id] 
                        : null;
                      
                      const resourceFirstName = details?.first_name || selectedResource?.first_name || '';
                      const resourceLastName = details?.last_name || selectedResource?.last_name || '';
                      const resourceName = details?.name || selectedResource?.name || 
                                  `${resourceFirstName} ${resourceLastName}`.trim();
                      const profilePhoto = details?.avatar_url || selectedResource?.profile_photo;
                      
                      const initials = getInitials(resourceFirstName, resourceLastName);
                      
                      return (
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium overflow-hidden text-xs">
                            {profilePhoto ? (
                              <img src={profilePhoto} alt={initials} className="h-full w-full object-cover" />
                            ) : (
                              initials
                            )}
                          </div>
                          <span className="text-xs sm:text-sm">
                            {resourceName || `Technician ${selectedResourceId.slice(0, 4)}`}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  
                  <div className="pt-2 border-t mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">${selectedService.priceOption.price.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-gray-600">Tax (5%)</span>
                      <span className="font-medium">${(selectedService.priceOption.price * 0.05).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 pt-1 sm:mt-2 sm:pt-2 border-t">
                      <span className="font-semibold">Total</span>
                      <span className="font-semibold">${(selectedService.priceOption.price * 1.05).toFixed(2)}</span>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
        
        {isResourceModalOpen && <ResourceSelectionModal />}
      </div>
    );
  };

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = {
      firstName: !firstName ? 'First name is required' : '',
      lastName: !lastName ? 'Last name is required' : '',
      vehicleYear: !vehicleYear ? 'Vehicle year is required' : '',
      vehicleMake: !vehicleMake ? 'Vehicle make is required' : '',
      vehicleModel: !vehicleModel ? 'Vehicle model is required' : '',
      email: !email ? 'Email is required' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Invalid email address' : '',
      phone: !phone ? 'Phone number is required' : ''
    };
    
    setFormErrors(errors);
    
    // Check if any errors exist
    if (Object.values(errors).some(error => error)) {
      return;
    }
    
    if (!selectedService || !selectedTime || !selectedResourceId) {
      toast.error('Please complete all previous steps before booking');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const startTime = new Date(selectedTime);
      const endTime = new Date(startTime.getTime() + selectedService.priceOption.duration_minutes * 60000);
      
      const startDateStr = format(startTime, 'yyyy-MM-dd');
      const startTimeStr = format(startTime, 'HH:mm');
      const endDateStr = format(endTime, 'yyyy-MM-dd');
      const endTimeStr = format(endTime, 'HH:mm');
      
      // Check if a customer with this email or phone already exists
      let customerId = '';
      console.log('Checking for existing customer with:', {
        business_id: business?.id || '',
        email,
        phone
      });

      const existingCustomer = await findExistingCustomer(
        business?.id || '',
        email,
        phone
      );

      if (existingCustomer) {
        console.log('Found existing customer:', {
          id: existingCustomer.id,
          first_name: existingCustomer.first_name,
          last_name: existingCustomer.last_name,
          email: existingCustomer.email,
          phone: existingCustomer.phone
        });
        
        // Update the existing customer with any new information
        const updatedCustomer = await updateCustomerFromBooking(
          existingCustomer.id,
          {
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            address: address
          }
        );
        
        customerId = existingCustomer.id;
        console.log('Updated existing customer:', updatedCustomer);
      } else {
        // Create a new customer
        console.log('No matching customer found. Creating new customer');
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .insert({
            business_id: business?.id || '',
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone: phone,
          address: address,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('id')
        .single();
      
      if (customerError) throw customerError;
      if (!customerData?.id) throw new Error('Failed to create customer record');
        
        customerId = customerData.id;
      }
      
      // Create vehicle for the customer
      const { data: vehicleData, error: vehicleError } = await supabase
        .from('customer_vehicles')
        .insert({
          customer_id: customerId,
          make: vehicleMake,
          model: vehicleModel,
          year: vehicleYear,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('id')
        .single();
      
      if (vehicleError) throw vehicleError;
      
      if (!vehicleData?.id) throw new Error('Failed to create vehicle record');
      
      // Create mobile booking using the createMobileBooking function
      const result = await createMobileBooking({
        business_id: business?.id || '',
        customer_id: customerId,
        resource_id: selectedResourceId,
        title: `Online Booking - ${firstName} ${lastName}`,
        start_date: startDateStr,
        start_time: startTimeStr,
        end_date: endDateStr,
        end_time: endTimeStr,
        status: 'scheduled',
        notes: 'Created via online booking',
        customer_notes: notes || '',
        booking_address: address,
        // Pass the buffer time to the booking creation
        buffer_time: bufferTime,
        vehicles: [
          {
            vehicle_id: vehicleData.id,
            services: [
              {
                service_id: selectedService.service.id,
                name: selectedService.service.name,
                description: selectedService.service.description || '',
                price: selectedService.priceOption.price,
                tax_exempt: selectedService.service.tax_exempt || false,
                tax_rate: selectedService.service.tax_exempt ? 0 : 5,
                tax_amount: selectedService.service.tax_exempt ? 0 : (selectedService.priceOption.price * 0.05),
                total_amount: selectedService.service.tax_exempt ? 
                  selectedService.priceOption.price : 
                  selectedService.priceOption.price * 1.05,
                duration_minutes: selectedService.priceOption.duration_minutes
              }
            ]
          }
        ]
      });
      
      if (!result || !result.id) throw new Error('No booking data returned');
      
      // 🎯 Trigger booking automations after successful online booking
      if (business?.id && result) {
        try {
          // Calculate grand total (including tax)
          const subtotal = selectedService.priceOption.price;
          const grandTotal = selectedService.service.tax_exempt ? 
            subtotal : 
            subtotal * 1.05;
          
          // Find the selected resource details for automation
          const selectedResource = (() => {
            const byResourceId = availableResources.find(r => r.resource_id === selectedResourceId);
            if (byResourceId) return byResourceId;
            return availableResources.find(r => r.id === selectedResourceId);
          })();
          
          const details = selectedResource?.resource_id 
            ? resourceDetails[selectedResource.resource_id] 
            : null;
          
          const resourceFirstName = details?.first_name || selectedResource?.first_name || '';
          const resourceLastName = details?.last_name || selectedResource?.last_name || '';
          
          // Prepare booking data for automation trigger
          const realBookingData = {
            booking_id: result.id,
            title: `Online Booking - ${firstName} ${lastName}`,
            customer: {
              full_name: `${firstName} ${lastName}`,
              phone: phone,
              email: email
            },
            schedule: {
              drop_off_date: startDateStr,
              drop_off_time: startTimeStr,
              start_date: startDateStr,
              start_time: startTimeStr,
              end_date: endDateStr,
              end_time: endTimeStr
            },
            services: [{
              name: selectedService.service.name,
              price: selectedService.priceOption.price,
              duration_minutes: selectedService.priceOption.duration_minutes,
              vehicle: {
                id: vehicleData.id,
                make: vehicleMake,
                model: vehicleModel,
                year: vehicleYear
              }
            }],
            grand_total: grandTotal,
            status: 'scheduled',
            resources: [{
              name: `${resourceFirstName} ${resourceLastName}`.trim(),
              email: '', // Resource email not available in online booking
              phone: '' // Resource phone not available in online booking
            }],
            vehicles: [{
              make: vehicleMake || '',
              model: vehicleModel || '',
              year: parseInt(vehicleYear) || 0,
              color: '',
              display: `${vehicleYear || ''} ${vehicleMake || ''} ${vehicleModel || ''}`.trim()
            }],
            booking_address: address,
            source: 'online_booking'
          };

          console.log('🎯 ONLINE BOOKING: Triggering booking automations with data:', realBookingData);
          
          // Run automations in the background (doesn't block UI)
          triggerBookingAutomations(business.id, 'created', realBookingData)
            .catch((triggerError: any) => console.error('Error executing booking automations:', triggerError));
          
          console.log('🎯 ONLINE BOOKING: Booking automations triggered successfully');
        } catch (triggerError) {
          console.error('❌ ONLINE BOOKING: Failed to trigger automations:', triggerError);
          // Don't throw here to avoid blocking the booking flow
        }
      }
      
      // Track completion step
      if (business?.id) {
        await trackStepProgression(business.id, 'completed');
      }
      
      // Success
      toast.success('Booking confirmed!');
      
      // Redirect to success page
      setTimeout(() => {
        window.location.href = `/${businessSlug}/booking-success?booking=${result.id}`;
      }, 1500);
      
    } catch (err) {
      console.error('Error creating booking:', err);
      toast.error('Failed to create booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderCustomerStep = () => {
    if (!selectedTime || !selectedResourceId || !selectedService) {
    return (
        <div className="text-center py-8">
          <p className="text-red-600">Please complete all previous steps before entering customer information.</p>
          <button 
            onClick={() => setCurrentStep('datetime')}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            Go Back to Date & Time Selection
          </button>
        </div>
      );
    }
    
    // Find the selected resource
    const selectedResource = (() => {
      // First try to find by resource_id (preferred)
      const byResourceId = availableResources.find(r => r.resource_id === selectedResourceId);
      if (byResourceId) return byResourceId;
      
      // If not found, try by mobile_availability.id
      return availableResources.find(r => r.id === selectedResourceId);
    })();
    
    // Get resource details
    const details = selectedResource?.resource_id 
      ? resourceDetails[selectedResource.resource_id] 
      : null;
    
    const resourceFirstName = details?.first_name || selectedResource?.first_name || '';
    const resourceLastName = details?.last_name || selectedResource?.last_name || '';
    const resourceName = details?.name || selectedResource?.name || 
                        `${resourceFirstName} ${resourceLastName}`.trim();
    const profilePhoto = details?.avatar_url || selectedResource?.profile_photo;
    
    const startTime = new Date(selectedTime);
    const endTime = new Date(startTime.getTime() + selectedService.priceOption.duration_minutes * 60000);
    const initials = getInitials(resourceFirstName, resourceLastName);

    return (
      <div className="max-w-3xl mx-auto px-3 sm:px-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
          {/* Customer Information Form - Shown first on mobile */}
          <div className="md:order-2">
            <form onSubmit={handleSubmitBooking} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    First Name*
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={`block w-full rounded-md border ${formErrors.firstName ? 'border-red-300' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm`}
                    placeholder="Your first name"
                  />
                  {formErrors.firstName && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.firstName}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Last Name*
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={`block w-full rounded-md border ${formErrors.lastName ? 'border-red-300' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm`}
                    placeholder="Your last name"
                  />
                  {formErrors.lastName && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.lastName}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label htmlFor="vehicleYear" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Vehicle Year*
                  </label>
                  <input
                    id="vehicleYear"
                    type="text"
                    value={vehicleYear}
                    onChange={(e) => setVehicleYear(e.target.value)}
                    className={`block w-full rounded-md border ${formErrors.vehicleYear ? 'border-red-300' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm`}
                    placeholder="Year"
                  />
                  {formErrors.vehicleYear && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.vehicleYear}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="vehicleMake" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Vehicle Make*
                  </label>
                  <input
                    id="vehicleMake"
                    type="text"
                    value={vehicleMake}
                    onChange={(e) => setVehicleMake(e.target.value)}
                    className={`block w-full rounded-md border ${formErrors.vehicleMake ? 'border-red-300' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm`}
                    placeholder="Make"
                  />
                  {formErrors.vehicleMake && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.vehicleMake}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="vehicleModel" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Vehicle Model*
                  </label>
                  <input
                    id="vehicleModel"
                    type="text"
                    value={vehicleModel}
                    onChange={(e) => setVehicleModel(e.target.value)}
                    className={`block w-full rounded-md border ${formErrors.vehicleModel ? 'border-red-300' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm`}
                    placeholder="Model"
                  />
                  {formErrors.vehicleModel && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.vehicleModel}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Email Address*
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`block w-full rounded-md border ${formErrors.email ? 'border-red-300' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Phone Number*
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`block w-full rounded-md border ${formErrors.phone ? 'border-red-300' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm`}
                />
                {formErrors.phone && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.phone}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="notes" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Additional Notes <span className="text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Any special instructions for your service provider"
                />
              </div>
              
              <div className="pt-2 sm:pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || !firstName || !lastName || !vehicleYear || !vehicleMake || !vehicleModel || !email || !phone}
                  className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Confirming Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </form>
          </div>
          
          {/* Booking Summary - Shown second on mobile */}
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg md:order-1 mt-5 md:mt-0">
            <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Booking Summary</h3>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="pb-2 sm:pb-3 border-b border-gray-200">
                <h4 className="text-base sm:text-lg font-medium">{selectedService.service.name}</h4>
                <div className="text-gray-600 text-xs sm:text-sm mt-1">
                  {selectedService.priceOption.duration_minutes} minutes • ${selectedService.priceOption.price}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium overflow-hidden">
                  {profilePhoto ? (
                    <img src={profilePhoto} alt={initials} className="h-full w-full object-cover" />
                  ) : (
                    initials
                  )}
                </div>
                <div>
                  <div className="font-medium text-sm sm:text-base">
                    {resourceName || `Resource ${selectedResourceId.slice(0, 4)}`}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Your technician</div>
                </div>
              </div>
              
              <div>
                <div className="text-gray-600 text-xs sm:text-sm">Date</div>
                <div className="font-medium text-sm sm:text-base">
                  {startTime.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
              
              <div>
                <div className="text-gray-600 text-xs sm:text-sm">Time</div>
                <div className="font-medium text-sm sm:text-base">
                  {startTime.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })} - {endTime.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </div>
              </div>
              
              <div>
                <div className="text-gray-600 text-xs sm:text-sm">Location</div>
                <div className="font-medium text-sm sm:text-base">{address}</div>
              </div>
              
              <div className="pt-2 sm:pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-xs sm:text-sm">Subtotal</span>
                  <span className="font-medium text-sm sm:text-base">${selectedService.priceOption.price.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 sm:mt-2">
                  <span className="text-gray-600 text-xs sm:text-sm">Tax (5%)</span>
                  <span className="font-medium text-sm sm:text-base">${(selectedService.priceOption.price * 0.05).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 sm:mt-2 pt-1 sm:pt-2 border-t">
                  <span className="font-semibold text-sm sm:text-lg">Total</span>
                  <span className="font-semibold text-sm sm:text-lg">${(selectedService.priceOption.price * 1.05).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'location':
        return renderLocationStep();
      case 'category':
        return renderCategoryStep();
      case 'service':
        return renderServiceStep();
      case 'datetime':
        return renderDateTimeStep();
      case 'customer':
        return renderCustomerStep();
      default:
        return null;
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !business) {
    return <div>Error: {error || 'Business not found'}</div>;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top bar */}
      <div 
        className="h-24 w-full flex items-center justify-start px-6 sm:px-8" 
        style={{ backgroundColor: business?.booking_settings?.navbar_color || '#000000' }}
      >
        {business?.booking_settings?.logo_url && (
          <img 
            src={business.booking_settings.logo_url} 
            alt={`${business.name} logo`}
            className="h-16 max-w-xs object-contain"
            onError={(e) => {
              // Hide the image if it fails to load
              (e.target as HTMLImageElement).style.display = 'none';
              console.error('Failed to load business logo:', business.booking_settings.logo_url);
            }}
          />
        )}
      </div>
      
      <div className="flex-1 flex items-center justify-center">
      <div className="w-full max-w-3xl px-4 sm:px-6 lg:px-8">
        {/* Content */}
        <div className="mb-8">
          <p className="text-lg sm:text-xl text-center text-gray-700">
              {currentStep === 'location' ? 'Select An Address From The Drop Down' :
               currentStep === 'category' ? 'Select A Category' :
             currentStep === 'service' ? 'Choose Your Service' :
             currentStep === 'datetime' ? 'Pick a Date & Time' :
             'Enter Your Information'}
          </p>
        </div>

        {/* Form Content */}
        {renderStepContent()}

        {/* Next Button */}
        <div className="max-w-xl mx-auto mt-6">
            {currentStep !== 'service' && currentStep !== 'customer' && (
            <button
              onClick={handleNext}
              disabled={
                (currentStep === 'location' && (!isServiceable || isLoading)) ||
                  (currentStep === 'category' && !selectedCategory) ||
                  (currentStep === 'datetime' && (!selectedDate || !selectedTime))
              }
              className={`w-full py-3 px-4 rounded-md transition-colors ${
                currentStep === 'location'
                  ? isServiceable 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : isServiceable === false
                    ? 'bg-red-500 text-white cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                  : currentStep === 'category' && !selectedCategory
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : currentStep === 'datetime' && (!selectedDate || !selectedTime)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                currentStep === 'location'
                  ? isServiceable 
                    ? 'focus:ring-green-500' 
                    : isServiceable === false
                    ? 'focus:ring-red-500'
                    : 'focus:ring-blue-500'
                  : 'focus:ring-blue-500'
              }`}
            >
              {isLoading ? 'Checking availability...' : 'NEXT'}
            </button>
          )}
        </div>

        {/* Timeline Steps */}
        <div className="mt-12">
          <div className="flex justify-between items-center">
            <div className="flex-1 flex">
              {steps.map((item, index, array) => (
                <div key={item.step} className="flex-1 flex items-center">
                  <div className="flex-1">
                    <div className="w-full flex items-center">
                      <div className="relative flex-1">
                        <div className={`h-1 ${
                          currentStep === item.step ? 'bg-blue-500' :
                          array.findIndex(s => s.step === currentStep) > index ? 'bg-green-500' :
                          'bg-gray-200'
                        }`}></div>
                      </div>
                      <button
                        onClick={() => handleStepClick(item.step)}
                        disabled={array.findIndex(s => s.step === currentStep) <= index}
                        className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                          currentStep === item.step ? 'bg-blue-500 text-white' :
                          array.findIndex(s => s.step === currentStep) > index 
                            ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600' 
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {index + 1}
                      </button>
                    </div>
                    <p className={`mt-2 text-xs font-medium text-center ${
                      currentStep === item.step ? 'text-blue-500' :
                      array.findIndex(s => s.step === currentStep) > index ? 'text-green-500' :
                      'text-gray-500'
                    }`}>{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Service Summary Section */}
          {currentStep !== 'service' && currentStep !== 'customer' && currentStep !== 'datetime' && selectedTime && selectedResourceId && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-2">Service Summary</h3>
            {(() => {
              const selectedResource = (() => {
                // First try to find by resource_id (preferred)
                const byResourceId = availableResources.find(r => r.resource_id === selectedResourceId);
                if (byResourceId) return byResourceId;
                
                // If not found, try by mobile_availability.id
                return availableResources.find(r => r.id === selectedResourceId);
              })();
              
              console.log('=== DIAGNOSTIC: Selected Resource ID:', selectedResourceId);
              console.log('=== DIAGNOSTIC: Available Resources:', availableResources);
              console.log('=== DIAGNOSTIC: Found Resource:', selectedResource);
              
              // Get resource details from our fetched data
              const details = selectedResource?.resource_id 
                ? resourceDetails[selectedResource.resource_id] 
                : null;
              
                const resourceFirstName = details?.first_name || selectedResource?.first_name || '';
                const resourceLastName = details?.last_name || selectedResource?.last_name || '';
              const resourceName = details?.name || selectedResource?.name || 
                                    `${resourceFirstName} ${resourceLastName}`.trim();
              const profilePhoto = details?.avatar_url || selectedResource?.profile_photo;
              
              const startTime = new Date(selectedTime);
              const endTime = new Date(startTime.getTime() + (selectedService?.priceOption.duration_minutes || 0) * 60000);
                const initials = getInitials(resourceFirstName, resourceLastName);
              
              return (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <span className="font-medium">Resource: </span>
                    {selectedResource ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium overflow-hidden">
                          {profilePhoto ? (
                            <img src={profilePhoto} alt={initials} className="h-full w-full object-cover" />
                          ) : (
                            initials
                          )}
                        </div>
                        <span>
                          {resourceName || `Resource ${selectedResourceId.slice(0, 4)}`}
                        </span>
                      </div>
                    ) : (
                      <span className="text-red-500">Resource name not found (ID: {selectedResourceId.slice(0, 8)}...)</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Start: </span>
                    {startTime.toLocaleString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                  <div>
                    <span className="font-medium">End: </span>
                    {endTime.toLocaleString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                  <div className="pt-2 border-t">
                    <span className="font-medium">Total: </span>
                    ${selectedService?.priceOption.price || 0}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        </div>
      </div>
    </div>
  );
} 